import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEpisodes } from '../../hooks/useEpisodes';
import { useCategories } from '../../hooks/useCategories';
import SEOHead from '../../components/seo/SEOHead';
import SchemaOrg from '../../components/seo/SchemaOrg';

export default function EpisodesPage() {
  const { hasAccess, loading, subscriberLoading } = useAuth();
  const { getVisibleEpisodes, loading: episodesLoading } = useEpisodes();
  const { categories } = useCategories();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Get visible episodes using the same logic as Home page
  const visibleEpisodes = getVisibleEpisodes();

  // Show loading while authentication is being checked
  if (loading || subscriberLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-loader-4-line animate-spin text-white text-2xl"></i>
          </div>
          <p className="text-gray-600">Checking your subscription...</p>
        </div>
      </div>
    );
  }

  // Redirect to subscribe if no access
  if (!hasAccess) {
    return <Navigate to="/subscribe" replace />;
  }

  // Filter episodes based on search and category
  const filteredEpisodes = visibleEpisodes.filter(episode => {
    const matchesSearch = episode.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         episode.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         episode.guest.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = !selectedCategory || episode.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // SEO Schema.org data for episodes page
  const episodesPageSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Episodes - DBD Podcast",
    "description": "Browse all available episodes of the DBD Podcast featuring conversations with industry leaders, entrepreneurs, and business experts.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes`,
    "mainEntity": {
      "@type": "ItemList",
      "name": "DBD Podcast Episodes",
      "description": "Complete collection of DBD Podcast episodes",
      "numberOfItems": visibleEpisodes.length,
      "itemListElement": visibleEpisodes.slice(0, 10).map((episode, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "PodcastEpisode",
          "name": episode.title,
          "description": episode.description,
          "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes/${episode.id}`,
          "datePublished": episode.date,
          "duration": episode.duration,
          "partOfSeries": {
            "@type": "PodcastSeries",
            "name": "DBD Podcast",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`
          }
        }
      }))
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/`
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Episodes",
        "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes`
      }
    ]
  };

  // Main content when user has access
  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <SEOHead 
        title="Episodes - DBD Podcast | Business Insights & Expert Conversations"
        description="Browse all available episodes of the DBD Podcast featuring conversations with industry leaders, entrepreneurs, and business experts. Premium content for subscribers."
        keywords="podcast episodes, business conversations, entrepreneurship interviews, leadership insights, industry experts, premium content"
        ogTitle="Episodes - DBD Podcast"
        ogDescription="Browse all available episodes of the DBD Podcast featuring conversations with industry leaders and business experts."
        ogImage="https://static.readdy.ai/image/ad65d83c1087407616f0591309583e77/5910930edf8f8435ab6c0a56926fd219.png"
        ogType="website"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/episodes`}
      />
      <SchemaOrg data={episodesPageSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            All Episodes
          </h1>
          <p className="text-xl text-amber-100 max-w-3xl mx-auto">
            Explore our complete collection of conversations with industry leaders, 
            entrepreneurs, and innovators shaping the future of business.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Search and filter controls */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400"></i>
                </div>
                <input
                  type="text"
                  placeholder="Search episodes by title, guest, or topic..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="relative">
                <select
                  className="appearance-none bg-white px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm cursor-pointer"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <i className="ri-arrow-down-s-line text-gray-400"></i>
                </div>
              </div>
              
              <div className="text-sm text-gray-600">
                {filteredEpisodes.length} episode{filteredEpisodes.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Episodes list */}
        {episodesLoading ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-loader-4-line animate-spin text-white text-2xl"></i>
            </div>
            <p className="text-gray-600">Loading episodes...</p>
          </div>
        ) : filteredEpisodes.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-gray-400 text-2xl"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No episodes found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
          </div>
        ) : (
          <div className={`grid gap-8 ${
            filteredEpisodes.length === 1 
              ? 'grid-cols-1 justify-items-center max-w-md mx-auto'
              : filteredEpisodes.length === 2 
                ? 'grid-cols-1 md:grid-cols-2 justify-items-center max-w-4xl mx-auto'
                : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {filteredEpisodes.map((episode) => (
              <div key={episode.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group">
                {/* Episode Image */}
                <div className="aspect-w-16 aspect-h-9 bg-gradient-to-br from-amber-100 to-amber-200 relative overflow-hidden">
                  <img
                    src={episode.advertisingImage}
                    alt={episode.title}
                    className="w-full h-48 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-black/70 text-white text-sm px-2 py-1 rounded">
                    {episode.duration}
                  </div>
                  {/* On Focus Badge */}
                  {episode.on_focus && (
                    <div className="absolute top-4 left-4 bg-amber-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      Featured
                    </div>
                  )}
                </div>
                
                {/* Episode Content */}
                <div className="p-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      {episode.category}
                    </span>
                    <span className="text-sm text-gray-500">{episode.date}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {episode.title}
                  </h3>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {episode.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                        <i className="ri-user-voice-line text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">{episode.guest}</p>
                        <p className="text-xs text-gray-500">Guest</p>
                      </div>
                    </div>
                    
                    <Link 
                      to={`/episodes/${episode.id}`} 
                      className="flex items-center space-x-1 text-amber-600 hover:text-amber-700 font-medium text-sm transition-colors group cursor-pointer"
                    >
                      <span>View or Listen</span>
                      <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                  
                  {/* View Count */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <i className="ri-eye-line"></i>
                      <span>{episode.viewCount || 0} views</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <i className="ri-time-line"></i>
                      <span>{new Date(episode.date).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}